import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/cms/ui/alert-dialog';
import {
  ClockCounterClockwiseIcon,
  FileDashedIcon,
  LockIcon,
} from '@phosphor-icons/react';
import { DesktopOnlyHint } from '@/components/handoff/DesktopOnlyHint';
import { Tile } from '@/components/ui/tile';
import { isMobileDevice } from '@/utils/deviceDetection';

interface LiveStoryActionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storyName: string;
  status: 'live' | 'pending';
  onAction: (action: 'unpublish' | 'archive' | 'edit-copy') => void;
}

export function LiveStoryActionsDialog({
  open,
  onOpenChange,
  storyName,
  status,
  onAction,
}: LiveStoryActionsDialogProps) {
  const handleAction = (action: 'unpublish' | 'archive' | 'edit-copy') => {
    onOpenChange(false);
    onAction(action);
  };

  const title = status === 'pending' ? 'Open Pending Story' : 'Open Live Story';
  const statusLabel = status === 'pending' ? 'pending approval' : 'currently live';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="cursor-default select-none">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="font-semibold text-lureo-ink">"{storyName}"</span> is {statusLabel}.
            Choose how you'd like to work with it.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogBody className="gap-2">
          <Tile
            size="md"
            disabled={isMobileDevice()}
            onClick={() => handleAction('edit-copy')}
            glyph={<ClockCounterClockwiseIcon className="h-5 w-5 text-lureo-sky-ink" />}
            label="Edit a Copy"
            description="Create a new draft from this live version while keeping it published."
          />
          {isMobileDevice() && <DesktopOnlyHint className="block text-center" />}

          <Tile
            size="md"
            onClick={() => handleAction('unpublish')}
            glyph={<FileDashedIcon className="h-5 w-5 text-lureo-sky-ink" />}
            label="Unpublish"
            description="Take this story offline and return it to draft."
          />

          {status === 'live' && (
            <Tile
              size="md"
              onClick={() => handleAction('archive')}
              glyph={<LockIcon className="h-5 w-5 text-lureo-sky-ink" />}
              label="Archive"
              description="Archive this live version. It will no longer be viewable."
            />
          )}
        </AlertDialogBody>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
